import { omit } from 'lodash';
import { GraphQLWhatsAppHeader } from 'modules/graphql';
import { EmbeddedMessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { UpdateTemplatePayload } from 'modules/campaigns/lib/hooks/useGenerateSaveTemplatePayload/types';
import { Locale } from 'modules/domains/template';
import {
  EmailContentArgsInput,
  EmailContentDefinition,
  EmbeddedContentArgsInput,
  EmbeddedContentDefinition,
  HtmlEditor,
  InAppContentArgsInput,
  InAppContentDefinition,
  MessageMedium,
  PushContentArgsInput,
  PushContentDefinition,
  SMSContentArgsInput,
  SMSContentDefinition,
  WebPushContentArgsInput,
  WebPushContentDefinition,
  WhatsAppContentArgsInput,
  WhatsAppContentDefinition,
} from 'modules/graphql';
import {
  SelectedMessageMedium,
  TemplateAndContentFormDataLocale,
} from 'modules/templateEditor/common';
import { convertInAppDisplayIntoWebInAppDisplay } from 'modules/templateEditor/EditInApp/utils';
import { formatButtonValues, formatHeaderVal } from 'modules/templateEditor/EditWhatsApp/utils';

/** Parses locale info stored in live template into expected format for the update call */
export function getLocaleDataForRequestParams(
  projectLocales: Locale[],
  currLocaleTag: string,
  defaultLocaleTag = '',
  newLocaleExperience = false,
) {
  const projectHasLocales = !!projectLocales?.length;
  if (newLocaleExperience) {
    if (projectHasLocales) {
      return { name: currLocaleTag?.length ? currLocaleTag : defaultLocaleTag };
    } else {
      return undefined;
    }
  }

  // Check if current locale is the default locale
  if (!currLocaleTag?.length || currLocaleTag === defaultLocaleTag) {
    return undefined;
  } else {
    return { name: currLocaleTag };
  }
}

const getNormalizedJsonPayload = (payloadString: string | undefined) =>
  payloadString === '' ? '{}' : payloadString;

/*
  NOTE: campaigns/lib may be a temporary location for template functionality.
  Consultation from content devs:
    "Template editing should house the fetching as well as editing/saving. Template editing functions on its own and Campaigns is a consumer of template editing."
  Templates may want its own /lib section to house this functionality, acting as a safe to import location that campaigns (and other modules) could import.
 */
export const generateUpdateTemplatePayload = (
  liveTemplateData: TemplateAndContentFormDataLocale,
  templateMedium: SelectedMessageMedium,
  projectLocales: Locale[],
  currentLocale: string,
  templateHtmlEditor: HtmlEditor | undefined,
  formData?: TemplateAndContentFormDataLocale,
  defaultLocaleTag = '',
  newLocaleExperience = false,
): UpdateTemplatePayload => {
  // parse template data to match query expected formatting
  const templateData = formData || liveTemplateData;
  const reqLocaleTag =
    formData?.mediumData?.locale?.name ||
    liveTemplateData?.mediumData?.locale?.name ||
    currentLocale;

  if (templateMedium === MessageMedium.Email) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as EmailContentDefinition;
    const sanitizedContent = omit(preModifiedContent, 'emailContentId') as EmailContentArgsInput;
    const { htmlEditor, ...allOtherContent } = sanitizedContent;
    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content: {
        ...allOtherContent,
        htmlEditor: htmlEditor || templateHtmlEditor,
        locale: getLocaleDataForRequestParams(
          projectLocales,
          reqLocaleTag,
          defaultLocaleTag,
          newLocaleExperience,
        ),
      },
    };
  } else if (templateMedium === MessageMedium.InApp) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as InAppContentDefinition;
    const sanitizedContent = omit(preModifiedContent, 'inAppContentId') as InAppContentArgsInput;
    const { htmlEditor, ...allOtherContent } = sanitizedContent;
    const normalizedPayload = getNormalizedJsonPayload(sanitizedContent.payload);

    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content: {
        ...allOtherContent,
        payload: normalizedPayload,
        htmlEditor: htmlEditor || templateHtmlEditor,
        locale: getLocaleDataForRequestParams(
          projectLocales,
          reqLocaleTag,
          defaultLocaleTag,
          newLocaleExperience,
        ),
        // at the moment, the UI shows one "display" option for both
        // web in-app and in-app; i.e.: if an in-app is set to "bottom",
        // then it will show up in the "BottomRight" for web in-app.
        // so we convert the values from inAppSettings into those
        // required for web-inapp (from webInApp)
        webInAppDisplaySettings: convertInAppDisplayIntoWebInAppDisplay(
          allOtherContent.inAppDisplaySettings,
        ),
      },
    };
  } else if (templateMedium === MessageMedium.SMS) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as SMSContentDefinition;
    const {
      message = '',
      smsSender,
      verifiedContactCardId,
      ...remainingContent
    } = preModifiedContent;
    const content: SMSContentArgsInput = {
      /**
       * Remove omit once `optOutDisclaimer` and `smsContentId` are added to
       * `SMSContentArgsInput` in the GraphQL schema
       */
      ...omit(remainingContent, ['optOutDisclaimer', 'smsContentId']),
      message,
      smsSenderId: smsSender.id,
      verifiedContactCardId: verifiedContactCardId === -1 ? undefined : verifiedContactCardId,
      locale: getLocaleDataForRequestParams(
        projectLocales,
        reqLocaleTag,
        defaultLocaleTag,
        newLocaleExperience,
      ),
    };

    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content,
    };
  } else if (templateMedium === MessageMedium.Push) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as PushContentDefinition;
    const sanitizedContent = omit(preModifiedContent, 'pushContentId') as PushContentArgsInput;
    const { message = '', actionButtons, ...allOtherContent } = sanitizedContent;
    const normalizedPayload = getNormalizedJsonPayload(sanitizedContent.payload);
    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content: {
        ...allOtherContent,
        payload: normalizedPayload,
        actionButtons: actionButtons?.map((button) => ({
          ...button,
          action: omit(button.action, 'isCustomAction'),
        })),
        locale: getLocaleDataForRequestParams(
          projectLocales,
          reqLocaleTag,
          defaultLocaleTag,
          newLocaleExperience,
        ),
        message,
      },
    };
  } else if (templateMedium === MessageMedium.WebPush) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as WebPushContentDefinition;
    const sanitizedContent = omit(
      preModifiedContent,
      'webPushContentId',
    ) as WebPushContentArgsInput;

    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content: {
        ...sanitizedContent,
        locale: getLocaleDataForRequestParams(
          projectLocales,
          reqLocaleTag,
          defaultLocaleTag,
          newLocaleExperience,
        ),
      },
    };
  } else if (templateMedium === EmbeddedMessageMedium.Embedded) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as EmbeddedContentDefinition;
    const sanitizedContent = omit(
      preModifiedContent,
      'embeddedContentId',
    ) as EmbeddedContentArgsInput;
    const normalizedPayload = getNormalizedJsonPayload(sanitizedContent.payload);
    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      content: {
        ...sanitizedContent,
        payload: normalizedPayload,
      },
    };
  } else if (templateMedium === WhatsAppMedium.WhatsApp) {
    const { mediumData, messageType, ...nonMediumData } = templateData;
    const preModifiedContent = mediumData as WhatsAppContentDefinition;
    // We pull both category and whatsAppCategory from the content since whatsAppCategory is the value from load
    // and category is the updated value so we check both before sending with a priority on category
    // @ts-ignore
    const { whatsAppCategory, category, header, buttons, metaApprovalStatus } = preModifiedContent;
    const formattedButtons = formatButtonValues(buttons || []);
    const formattedHeader = formatHeaderVal(header as GraphQLWhatsAppHeader);
    const sanitizedContent = omit(
      preModifiedContent,
      'whatsAppCategory',
      'previousCategory',
      'buttons',
      'header',
      'metaApprovalStatus',
      'qualityRating',
      'rejectionReason',
    ) as WhatsAppContentArgsInput;
    return {
      ...nonMediumData,
      messageTypeId: messageType.id as number,
      // @ts-ignore
      content: {
        ...sanitizedContent,
        category: category ?? whatsAppCategory,
        header: formattedHeader,
        buttons: formattedButtons,
        metaApprovalStatus,
        placeholders: [], // TODO: make this dynamic with fields once fully implememented
      },
    };
  } else {
    return {} as UpdateTemplatePayload;
  }
};
